import React, { FC, useState } from 'react';
import { Link, Route, Switch } from 'react-router-dom';
import {
  Badge,
  Button,
  ButtonGroup,
  Col,
  FormGroup,
  Input,
  Label,
  PopoverBody,
  PopoverHeader,
  Row,
  Table,
  UncontrolledPopover,
} from 'reactstrap';
import { DealerFilterTypes, DealerFullModel, dealerStatusList } from '../../../../shared/dealer';
import { MaterialIcon, PageHeader, UserStateComponent } from '../../../components';
import { useTranslation } from '../../../translations';
import {
  countBy,
  displayName,
  isNonEmpty,
  renderName,
  toggleElementInArray,
  useNumericInputState,
} from '../../../utils';
import { Fetcher, useFetcher } from '../../../utils/useFetcher';
import { DealerDetails } from './dealerDetails';
import { TableTypeArchiveWarning } from './util';

export const Dealers: FC = () => (
  <UserStateComponent>
    <Switch>
      <Route component={DealersList} exact path="/housekeeping/dealers/list" />
      <Route component={DealerDetails} path="/housekeeping/dealers/list/:id" />
    </Switch>
  </UserStateComponent>
);

const DealersList: FC = () => {
  const [statusFilter, setStatusFilter] = useState<DealerFilterTypes[]>(
    dealerStatusList.map((t) => t.key as DealerFilterTypes).concat(['paid', 'unpaid']),
  );

  const [areaId, setAreaIdEV, setAreaId] = useNumericInputState(0, true);

  const dealers = useFetcher(async () => {
    return areaId ? await api.getAllDealers([areaId]) : [];
  }, [areaId]);

  const fetcher = useFetcher(async () => {
    const result = await api.getDealerAreas();
    if (isNonEmpty(result)) {
      setAreaId(result[0].id);
    }

    return result;
  });

  const { ts } = useTranslation();
  return (
    <>
      <PageHeader>{ts('dealers')}</PageHeader>
      {fetcher.data ? (
        <>
          <Row className="justify-content-between">
            <Col xs={3}>
              <Row className="align-items-center">
                <Col className="col-md-auto align-self-center align-middle">
                  <Label for="dealerAreaSelect">{ts('filter_by_area')}:</Label>
                </Col>
                <Col className="p-0">
                  <Input id="dealerAreaSelect" onChange={setAreaIdEV} type="select" value={areaId}>
                    {fetcher.data.map((area) => (
                      <option key={area.id} value={area.id}>
                        {area.name}
                      </option>
                    ))}
                  </Input>
                </Col>
              </Row>
            </Col>
            <Col xs={9}>
              <DealersListStatusFilters
                areaId={areaId}
                dealers={dealers.data ?? []}
                selected={statusFilter}
                update={setStatusFilter}
              />
            </Col>
          </Row>
          <Fetcher result={dealers}>
            <DealersListTableRows
              areaId={areaId}
              dealers={dealers.data!}
              statusFilter={statusFilter}
            />
          </Fetcher>
        </>
      ) : (
        <Fetcher result={fetcher} />
      )}
    </>
  );
};

interface DealersListStatusFiltersProps {
  readonly areaId: number;
  readonly selected: DealerFilterTypes[];
  readonly update: (name: DealerFilterTypes[]) => void;
  readonly dealers: DealerFullModel[];
}

const DealersListStatusFilters: FC<DealersListStatusFiltersProps> = ({
  areaId,
  selected,
  update,
  dealers,
}) => {
  const { ts } = useTranslation();

  return (
    <>
      <Button id="openSettingsPopover" style={{ float: 'right', margin: '10px' }}>
        <MaterialIcon name="tune" type="white" />
        <UncontrolledPopover placement="bottom" target="openSettingsPopover" trigger="legacy">
          <PopoverHeader>{ts('attendee_search_filter_modal_title')}</PopoverHeader>
          <PopoverBody>
            <UpdateDealerSearchFilters dealers={dealers} selected={selected} update={update} />
          </PopoverBody>
        </UncontrolledPopover>
      </Button>
      <ButtonGroup className="p-10" style={{ float: 'right' }}>
        {dealerStatusList.map((filter) => {
          const active = selected.includes(filter.key);
          const cnt = countBy(dealers, (dealer) => {
            return dealer.applications.some((a) => a.status === filter.key && a.areaId === areaId);
          });

          return (
            <Button
              active={active}
              color={active ? 'primary' : ''}
              id={`${filter.key}button`}
              key={filter.key}
              onClick={() => {
                update(toggleElementInArray(selected, filter.key));
              }}
            >
              {filter.name} ({cnt})
            </Button>
          );
        })}
      </ButtonGroup>
    </>
  );
};

interface DealersListTableRowsProps {
  readonly dealers: DealerFullModel[];
  readonly statusFilter: string[];
  readonly areaId: number;
}

const DealersListTableRows: FC<DealersListTableRowsProps> = ({ dealers, statusFilter, areaId }) => {
  const { ts } = useTranslation();

  const rows: React.JSX.Element[] = [];
  const filteredDealers = dealers;

  filteredDealers.sort((a, b) => a.name.localeCompare(b.name));

  for (const dealer of filteredDealers) {
    for (const application of dealer.applications) {
      if (areaId !== application.areaId) {
        continue;
      }

      if (!statusFilter.includes(application.status)) {
        continue;
      }

      if (!statusFilter.includes('paid') && dealer.registration?.paid) {
        continue;
      }

      if (!statusFilter.includes('unpaid') && !dealer.registration?.paid) {
        continue;
      }

      rows.push(
        <tr id={`application${application.id}`} key={`${dealer.id}-${application.id}`}>
          <td>
            <Link to={`/housekeeping/dealers/list/${dealer.id}`}>{dealer.name}</Link>
          </td>
          <td>
            <Link to={`/housekeeping/attendees/user/${dealer.user.id}`}>
              {renderName(dealer.user)}
            </Link>{' '}
            {dealer.user.hasRestrictionNotice ? (
              <Badge className="has-restriction-notice" color="danger" key="hasRestrictionNotice">
                {ts('common.restriction')}
              </Badge>
            ) : undefined}
          </td>
          <td>
            {application.status}{' '}
            {application.status === 'approved' && application.paidOrderItemId && '(Paid)'}
            {application.status === 'approved' && !dealer.registration?.paid && (
              <UnpaidRegistration />
            )}
          </td>
          <td>{application.dealerArea.name}</td>
          <td>
            {displayName(application.tableType)}
            <TableTypeArchiveWarning idx={application.id} tableType={application.tableType} />
          </td>
          <td>{application.tableNumber}</td>
        </tr>,
      );
    }
  }

  return (
    <Table>
      <thead>
        <tr>
          <th>{ts('dealer.store')}</th>
          <th>{ts('common.user')}</th>
          <th>{ts('common.status')}</th>
          <th>{ts('area')}</th>
          <th>{ts('dealer.table_type')}</th>
          <th>{ts('table')}</th>
        </tr>
      </thead>
      <tbody>{rows}</tbody>
    </Table>
  );
};

const UnpaidRegistration: FC = () => {
  const { ts } = useTranslation();
  return (
    <>
      <span className="inline-icon" id="attendanceTypeMismatch">
        <MaterialIcon name="warning" type="warning" />
      </span>
      <UncontrolledPopover placement="bottom" target="attendanceTypeMismatch" trigger="hover">
        <PopoverBody>
          <p>{ts('this_user_does_not_have')}</p>
        </PopoverBody>
      </UncontrolledPopover>
    </>
  );
};

interface UpdateDealerSearchFiltersProps {
  readonly selected: DealerFilterTypes[];
  readonly update: (name: DealerFilterTypes[]) => void;
  readonly dealers: DealerFullModel[];
}

const UpdateDealerSearchFilters: FC<UpdateDealerSearchFiltersProps> = ({
  selected,
  update,
  dealers,
}) => {
  const paidRegistrationDealers = countBy(dealers, (dealer) => !!dealer.registration?.paid);
  const unpaidRegistrationDealers = countBy(dealers, (dealer) => !dealer.registration?.paid);

  return (
    <>
      <FormGroup switch>
        <Input
          className="form-switch-input"
          defaultChecked={selected.includes('unpaid')}
          id="only-unpaid-users"
          name="only-unpaid-users"
          onChange={() => {
            update(toggleElementInArray(selected, 'unpaid'));
          }}
          type="switch"
        />
        <Label check>Users with unpaid registration ({unpaidRegistrationDealers})</Label>
      </FormGroup>
      <FormGroup switch>
        <Input
          className="form-switch-input"
          defaultChecked={selected.includes('paid')}
          id="only-paid-users"
          name="only-paid-users"
          onChange={() => {
            update(toggleElementInArray(selected, 'paid'));
          }}
          type="switch"
        />
        <Label check>Users with paid registration ({paidRegistrationDealers})</Label>
      </FormGroup>
    </>
  );
};
